import { useEffect, useMemo, useRef, useState } from "react";
import client from "@/lib/api";
import { useAuth, startLogin } from "@/lib/auth";
import {
  PHASE_TIMERS, PHASE_LABELS, phaseForStreak,
  pickRandomCountry, countryNameOf, phaseOf,
} from "@/lib/arcadeCountries";
import { Target, Zap, Clock, Flame, Trophy, RotateCcw, Lock, Crosshair, Globe2 } from "lucide-react";

const GAME_DURATION_S = 90; // total run length

const COMBO_MULT = (combo) => {
  if (combo >= 20) return 3;
  if (combo >= 10) return 2;
  if (combo >= 5) return 1.5;
  return 1;
};

/**
 * Operation Atlas — game state machine + HUD overlays.
 * Renders ABOVE the existing globe in /wiki/world when mode === "atlas".
 *
 * Game lifecycle:
 *   "intro"   → start screen
 *   "playing" → active round (target shown, timer running)
 *   "feedback"→ brief reveal after answer (correct/wrong/timeout)
 *   "over"    → game-over summary
 */
export default function OperationAtlas({ features, globeRef, onExit }) {
  const { user } = useAuth();
  const login = startLogin;
  const [state, setState] = useState("intro");
  const [target, setTarget] = useState(null);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [streak, setStreak] = useState(0);          // total consecutive correct (drives phase)
  const [correct, setCorrect] = useState(0);
  const [timer, setTimer] = useState(10);
  const [maxPhase, setMaxPhase] = useState(1);
  const [feedback, setFeedback] = useState(null);   // { kind: "correct"|"wrong"|"timeout", earned, country }
  const [countriesSeen, setCountriesSeen] = useState(new Set());
  // History exclusion loop — tracks every country used as a target this run.
  // Kept in a ref (not state) so nextRound() can read the latest set inside a
  // React 18 batched setState without stale closures. Cleared on startGame().
  const usedTargetsRef = useRef(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pb, setPb] = useState(null);
  const [totalTimeLeft, setTotalTimeLeft] = useState(GAME_DURATION_S);
  const startedAtRef = useRef(null);

  const phase = useMemo(() => phaseForStreak(streak), [streak]);
  const phaseTime = PHASE_TIMERS[phase];

  // -------- Total-run timer (90s hard cap) --------
  useEffect(() => {
    if (state !== "playing" && state !== "feedback") return;
    if (totalTimeLeft <= 0) {
      endGame();
      return;
    }
    const id = setTimeout(() => setTotalTimeLeft(t => t - 1), 1000);
    return () => clearTimeout(id);
  /* eslint-disable-next-line */
  }, [state, totalTimeLeft]);

  // -------- Timer loop --------
  useEffect(() => {
    if (state !== "playing") return;
    if (timer <= 0) {
      handleTimeout();
      return;
    }
    const id = setTimeout(() => setTimer(t => t - 1), 1000);
    return () => clearTimeout(id);
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state, timer]);

  // -------- Globe click integration --------
  // The parent World.jsx will call onGuess() via window event when in atlas mode.
  useEffect(() => {
    if (state !== "playing") return;
    const handler = (e) => {
      const feature = e.detail?.feature;
      if (!feature) return;
      handleGuess(feature);
    };
    window.addEventListener("atlas-globe-click", handler);
    return () => window.removeEventListener("atlas-globe-click", handler);
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state, target, timer, combo]);

  const startGame = () => {
    if (!user) { login?.(); return; }
    setState("playing");
    setScore(0); setCombo(0); setBestCombo(0); setStreak(0);
    setCorrect(0); setMaxPhase(1); setFeedback(null);
    setCountriesSeen(new Set());
    usedTargetsRef.current = new Set();  // reset history for new run
    setSubmitted(false); setPb(null);
    startedAtRef.current = Date.now();
    nextRound(1, null);
  };

  const nextRound = (currentPhase, justUsedName) => {
    // Push the country we just used into the exclusion pool so it can't repeat
    // sequentially. When the pool for this phase is exhausted, clear the
    // history so play can continue (per spec: "until pool depleted OR run
    // resets").
    if (justUsedName) usedTargetsRef.current.add(justUsedName);
    let next = pickRandomCountry(features, currentPhase, usedTargetsRef.current);
    if (!next) {
      // Pool for this difficulty is fully consumed → recycle history.
      usedTargetsRef.current = new Set();
      next = pickRandomCountry(features, currentPhase, usedTargetsRef.current);
    }
    if (next) {
      const chosenName = countryNameOf(next);
      if (chosenName) usedTargetsRef.current.add(chosenName);
    }
    setTarget(next);
    setTimer(PHASE_TIMERS[currentPhase]);
    setFeedback(null);
  };

  const handleGuess = (feature) => {
    const targetName = countryNameOf(target);
    const guessName = countryNameOf(feature);
    const remaining = timer;

    if (guessName === targetName) {
      // CORRECT
      const mult = COMBO_MULT(combo + 1);
      const earned = Math.round((100 + remaining * 10) * mult);
      const newCombo = combo + 1;
      const newStreak = streak + 1;
      const newPhase = phaseForStreak(newStreak);
      setScore(s => s + earned);
      setCombo(newCombo);
      setBestCombo(b => Math.max(b, newCombo));
      setStreak(newStreak);
      setCorrect(c => c + 1);
      setMaxPhase(p => Math.max(p, newPhase));
      setCountriesSeen(prev => { const n = new Set(prev); n.add(guessName); return n; });
      setFeedback({ kind: "correct", earned, country: guessName, mult });
      setState("feedback");
      setTimeout(() => { setState("playing"); nextRound(newPhase, guessName); }, 700);
    } else {
      // WRONG
      setScore(s => Math.max(0, s - 50));
      setCombo(0); setStreak(0);
      setFeedback({ kind: "wrong", correctName: targetName, guessedName: guessName });
      setState("feedback");
      setTimeout(() => { setState("playing"); nextRound(1, targetName); }, 1200);
    }
  };

  const handleTimeout = () => {
    setCombo(0); setStreak(0);
    setFeedback({ kind: "timeout", correctName: countryNameOf(target) });
    setState("feedback");
    setTimeout(() => { setState("playing"); nextRound(1, countryNameOf(target)); }, 1200);
  };

  const endGame = async () => {
    setState("over");
    const duration = startedAtRef.current ? Math.round((Date.now() - startedAtRef.current) / 1000) : 0;
    if (!user) return;
    setSubmitting(true);
    try {
      const r = await client.post("/arcade/run", {
        score, correct, best_combo: bestCombo, max_phase: maxPhase,
        duration_s: duration,
        countries_seen: Array.from(countriesSeen),
      });
      setPb(r.data?.personal_best);
      setSubmitted(true);
    } catch (_) { /* swallow */ }
    finally { setSubmitting(false); }
  };

  // -------- Render branches --------

  if (state === "intro") {
    return <IntroOverlay user={user} login={login} onStart={startGame} onExit={onExit} />;
  }

  if (state === "over") {
    return (
      <GameOverOverlay
        score={score} correct={correct} bestCombo={bestCombo} maxPhase={maxPhase}
        submitted={submitted} submitting={submitting} pb={pb}
        onPlayAgain={startGame} onExit={onExit}
      />
    );
  }

  // Active game (playing OR feedback)
  return (
    <ActiveHUD
      target={target} timer={timer} phase={phase} phaseTime={phaseTime}
      score={score} combo={combo} feedback={feedback}
      totalTimeLeft={totalTimeLeft} totalDuration={GAME_DURATION_S}
      onAbort={endGame}
    />
  );
}

/* ============================== INTRO ============================== */

function IntroOverlay({ user, login, onStart, onExit }) {
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center p-4 md:p-6 bg-[rgba(2,9,17,0.72)] backdrop-blur-sm" data-testid="atlas-intro">
      <div className="panel corner-frame max-w-lg w-full p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[radial-gradient(circle,_rgba(102,230,255,0.20)_0%,_transparent_70%)] pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-[var(--t-border-glow)] mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] pulse-glow" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-[var(--t-cyan)] uppercase">// Territoria Arcade</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--t-text)] uppercase leading-tight">Operation Atlas</h2>
          <p className="mt-3 text-sm md:text-base text-[var(--t-text-dim)] leading-relaxed">
            Help Territoria map and secure the world before launch. Identify the target region on the
            globe before the timer runs out. Chain correct answers to climb difficulty phases.
          </p>

          <ul className="mt-4 space-y-1.5 font-mono text-xs text-[var(--t-text-dim)]">
            <li><span className="text-[#39FF14]">▸</span> <span className="text-[var(--t-cyan)]">90 second</span> run · per-target timer counts down phase by phase</li>
            <li><span className="text-[#5fd99a]">▸</span> Correct: <span className="text-[var(--t-cyan)]">+100</span> + remaining seconds × 10</li>
            <li><span className="text-[var(--t-red)]">▸</span> Wrong: <span className="text-[var(--t-red)]">−50</span>, combo reset</li>
            <li><span className="text-[var(--t-gold)]">▸</span> Combo bonus: x1.5 @ 5, x2 @ 10, x3 @ 20</li>
            <li><span className="text-[#a780ff]">▸</span> Phases: I (10s) → II (8s) → III (6s) → IV (4s)</li>
          </ul>

          <div className="mt-6 flex flex-wrap gap-3">
            {user ? (
              <button onClick={onStart} className="btn-primary" data-testid="atlas-begin">
                <Crosshair className="w-4 h-4" /> Begin Operation Atlas
              </button>
            ) : (
              <button onClick={login} className="btn-primary" data-testid="atlas-login">
                <Lock className="w-4 h-4" /> Sign in with Discord
              </button>
            )}
            <button onClick={onExit} className="btn-ghost" data-testid="atlas-exit">
              <Globe2 className="w-4 h-4" /> Back to Map
            </button>
          </div>
          {!user && (
            <p className="mt-3 font-mono text-[10px] tracking-[0.18em] text-[var(--t-text-muted)] uppercase">
              Discord required to submit scores to the operator leaderboard.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================== ACTIVE HUD ============================== */

function ActiveHUD({ target, timer, phase, phaseTime, score, combo, feedback, totalTimeLeft, totalDuration, onAbort }) {
  const targetName = countryNameOf(target);
  const phaseLabel = PHASE_LABELS[phase];
  const mult = COMBO_MULT(combo);
  const lowTime = timer <= 3;
  const lowTotal = totalTimeLeft <= 10;
  const totalPct = totalDuration > 0 ? Math.max(0, Math.min(1, totalTimeLeft / totalDuration)) : 0;

  return (
    <>
      {/* Top HUD strip (target + timer) */}
      <div
        className="absolute left-0 right-0 top-0 z-20 px-3 md:px-5 py-2.5 md:py-3 border-b border-[rgba(102,230,255,0.25)] bg-[rgba(2,9,17,0.85)] backdrop-blur-md flex items-center justify-between gap-3"
        data-testid="atlas-hud"
      >
        <div className="flex items-center gap-3 md:gap-5 min-w-0">
          <div className="min-w-0">
            <div className="font-mono text-[9px] md:text-[10px] tracking-[0.3em] text-[#39FF14] uppercase">// Target_Region</div>
            <div className="font-display text-lg md:text-2xl text-[#39FF14] uppercase truncate leading-tight" style={{textShadow:"0 0 14px rgba(57,255,20,0.5)"}} data-testid="atlas-target">
              {targetName || "—"}
            </div>
          </div>
          <div className="hidden md:block w-px h-8 bg-[rgba(102,230,255,0.25)]" />
          <div>
            <div className="font-mono text-[9px] md:text-[10px] tracking-[0.3em] text-[var(--t-text-dim)] uppercase">// Phase</div>
            <div className="font-mono text-xs md:text-sm text-[var(--t-cyan)] uppercase tracking-[0.18em] whitespace-nowrap">{phaseLabel}</div>
          </div>
        </div>

        <button onClick={onAbort} className="btn-ghost !text-[10px] md:!text-xs !py-1.5 !px-2.5 md:!px-3 shrink-0" data-testid="atlas-abort">
          <RotateCcw className="w-3 h-3" /> End Run
        </button>
      </div>

      {/* Global run-timer bar — directly under the HUD strip */}
      <div className="absolute left-0 right-0 top-[56px] md:top-[64px] z-20 px-3 md:px-5 py-1.5 bg-[rgba(2,9,17,0.85)] backdrop-blur-md border-b border-[rgba(102,230,255,0.18)]" data-testid="atlas-total-timer">
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-[var(--t-text-dim)]">
            // Run_Time_Remaining
          </span>
          <span
            className={`font-display text-sm md:text-base ${lowTotal ? "pulse-glow" : ""}`}
            style={{ color: lowTotal ? "#e35a5a" : "#66e6ff", textShadow: lowTotal ? "0 0 10px #e35a5a" : "0 0 8px rgba(102,230,255,0.5)" }}
          >
            {Math.floor(totalTimeLeft / 60)}:{String(totalTimeLeft % 60).padStart(2, "0")}
          </span>
        </div>
        <div className="h-1 bg-[rgba(102,230,255,0.15)] overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${totalPct * 100}%`,
              background: lowTotal
                ? "linear-gradient(90deg, #e35a5a, #ff8a80)"
                : "linear-gradient(90deg, #1a4f87, #66e6ff)",
              boxShadow: lowTotal ? "0 0 12px #e35a5a" : "0 0 10px rgba(102,230,255,0.5)",
            }}
          />
        </div>
      </div>

      {/* Stats column (right side on desktop, bottom on mobile) */}
      <div className="absolute z-20 right-3 md:right-5 top-[100px] md:top-[112px] flex flex-col gap-2 md:gap-3 pointer-events-none">
        <HudStat icon={Clock}  label="Q-Time" value={`${timer}s`}  accent={lowTime ? "var(--t-red)" : "var(--t-cyan)"} pulse={lowTime} />
        <HudStat icon={Zap}    label="Score" value={score.toLocaleString()} accent="var(--t-blue-glow)" />
        <HudStat icon={Flame}  label="Combo" value={`x${combo}`} sub={mult > 1 ? `mult ${mult}x` : null} accent={combo >= 5 ? "#f3c552" : "var(--t-text-dim)"} />
      </div>

      {/* Timer ring (large, center-top) */}
      <div className="absolute z-10 left-1/2 -translate-x-1/2 top-28 md:top-32 pointer-events-none" data-testid="atlas-timer">
        <TimerRing remaining={timer} total={phaseTime} low={lowTime} />
      </div>

      {/* Feedback flash */}
      {feedback && <FeedbackFlash feedback={feedback} />}
    </>
  );
}

function HudStat({ icon: Icon, label, value, sub, accent, pulse }) {
  return (
    <div
      className="panel !py-1.5 !px-2.5 md:!py-2 md:!px-3 min-w-[110px] md:min-w-[130px]"
      style={{ borderColor: accent, boxShadow: pulse ? `0 0 18px ${accent}` : undefined }}
    >
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className="w-3 h-3" style={{ color: accent }} />
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--t-text-muted)]">{label}</span>
      </div>
      <div className="font-display text-base md:text-lg leading-tight" style={{ color: accent }}>{value}</div>
      {sub && <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--t-gold)]">{sub}</div>}
    </div>
  );
}

function TimerRing({ remaining, total, low }) {
  const radius = 32;
  const stroke = 5;
  const c = 2 * Math.PI * radius;
  const pct = total > 0 ? Math.max(0, remaining / total) : 0;
  const color = low ? "#e35a5a" : "#66e6ff";
  return (
    <div className="relative w-[80px] h-[80px] md:w-[88px] md:h-[88px]">
      <svg viewBox="0 0 80 80" className="absolute inset-0 -rotate-90">
        <circle cx="40" cy="40" r={radius} stroke="rgba(102,230,255,0.18)" strokeWidth={stroke} fill="none" />
        <circle cx="40" cy="40" r={radius} stroke={color} strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 1s linear", filter: `drop-shadow(0 0 6px ${color})` }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-display text-2xl md:text-3xl" style={{ color, textShadow: `0 0 10px ${color}80` }}>
        {remaining}
      </div>
    </div>
  );
}

function FeedbackFlash({ feedback }) {
  const { kind } = feedback;
  if (kind === "correct") {
    return (
      <div className="absolute z-30 inset-0 pointer-events-none flex items-center justify-center" data-testid="atlas-feedback-correct">
        <div className="panel px-6 py-4 border-[#5fd99a]" style={{ boxShadow: "0 0 28px #5fd99a, inset 0 0 18px rgba(95,217,154,0.25)" }}>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#5fd99a] uppercase">// Region_Confirmed</div>
          <div className="font-display text-2xl md:text-3xl text-[#5fd99a] uppercase" style={{ textShadow: "0 0 14px #5fd99a" }}>
            +{feedback.earned}
            {feedback.mult > 1 && <span className="ml-2 text-base text-[var(--t-gold)]">×{feedback.mult}</span>}
          </div>
        </div>
      </div>
    );
  }
  if (kind === "wrong") {
    return (
      <div className="absolute z-30 inset-0 pointer-events-none flex items-center justify-center" data-testid="atlas-feedback-wrong">
        <div className="panel px-6 py-4 border-[var(--t-red)]" style={{ boxShadow: "0 0 28px var(--t-red), inset 0 0 18px rgba(227,90,90,0.25)" }}>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[var(--t-red)] uppercase">// Misidentified</div>
          <div className="font-display text-xl md:text-2xl text-[var(--t-red)] uppercase">
            Target was <span className="text-[#39FF14]">{feedback.correctName}</span>
          </div>
        </div>
      </div>
    );
  }
  if (kind === "timeout") {
    return (
      <div className="absolute z-30 inset-0 pointer-events-none flex items-center justify-center" data-testid="atlas-feedback-timeout">
        <div className="panel px-6 py-4 border-[var(--t-gold)]" style={{ boxShadow: "0 0 28px var(--t-gold), inset 0 0 18px rgba(243,197,82,0.25)" }}>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[var(--t-gold)] uppercase">// Timeout</div>
          <div className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase">
            Target was <span className="text-[#39FF14]">{feedback.correctName}</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

/* ============================== GAME OVER ============================== */

function GameOverOverlay({ score, correct, bestCombo, maxPhase, submitted, submitting, pb, onPlayAgain, onExit }) {
  const isPb = submitted && score >= (pb || 0);
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center p-4 md:p-6 bg-[rgba(2,9,17,0.78)] backdrop-blur-sm" data-testid="atlas-over">
      <div className="panel corner-frame max-w-lg w-full p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[radial-gradient(circle,_rgba(58,160,255,0.22)_0%,_transparent_70%)] pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-[var(--t-border-glow)] mb-3">
            <Trophy className="w-3 h-3 text-[var(--t-gold)]" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-[var(--t-cyan)] uppercase">// Operation_Concluded</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--t-text)] uppercase leading-tight">Debrief</h2>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <Stat label="Final Score" value={score.toLocaleString()} accent="var(--t-cyan)" />
            <Stat label="Regions ID'd" value={correct} accent="#5fd99a" />
            <Stat label="Best Combo"  value={`x${bestCombo}`}  accent="#f3c552" />
            <Stat label="Max Phase"   value={`Phase ${["I","II","III","IV"][maxPhase-1] || "I"}`} accent="#a780ff" />
          </div>

          {submitting && (
            <div className="mt-4 font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase pulse-glow">// Uploading run...</div>
          )}
          {submitted && (
            <div className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase">
              {isPb
                ? <span className="text-[var(--t-gold)]" data-testid="atlas-pb-flag">// NEW personal best · {pb?.toLocaleString()}</span>
                : <span className="text-[var(--t-text-dim)]">// Submitted · personal best {pb?.toLocaleString()}</span>}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={onPlayAgain} className="btn-primary" data-testid="atlas-play-again">
              <Target className="w-4 h-4" /> Run Again
            </button>
            <button onClick={onExit} className="btn-ghost" data-testid="atlas-back-map">
              <Globe2 className="w-4 h-4" /> Back to Map
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="panel !py-2 !px-3" style={{ borderColor: accent }}>
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--t-text-muted)]">{label}</div>
      <div className="font-display text-xl md:text-2xl mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}
